/**
 * 全站搜索栏 — 首页核心
 * 输入即查,800ms 防抖,下拉显示学校 + 专业匹配
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { search, trackEvent, type SearchResult, ApiError } from '../lib/api'

export default function SearchBar() {
  const nav = useNavigate()
  const [q, setQ] = useState('')
  const [focused, setFocused] = useState(false)
  const [results, setResults] = useState<SearchResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(-1)
  const [err, setErr] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  const doSearch = useCallback(async (query: string) => {
    if (!query.trim() || query.trim().length < 1) {
      setResults(null)
      return
    }
    setLoading(true)
    setErr(null)
    try {
      const r = await search(query, 8)
      setResults({ schools: r.schools, majors: r.majors })
      setSelectedIdx(-1)
    } catch (e) {
      setErr(e instanceof ApiError ? String(e.message) : '搜索失败')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    if (q.trim().length < 1) {
      setResults(null)
      return
    }
    timer.current = setTimeout(() => doSearch(q.trim()), 800)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [q, doSearch])

  // 点击外部关闭 dropdown
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setFocused(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const total = (results?.schools.length ?? 0) + (results?.majors.length ?? 0)
  const show = focused && q.trim().length > 0 && (loading || results || err)

  function onKeyDown(e: React.KeyboardEvent) {
    if (!results || total === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx((prev) => (prev + 1) % total)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx((prev) => (prev - 1 + total) % total)
    } else if (e.key === 'Enter' && selectedIdx >= 0) {
      select(selectedIdx)
    }
  }

  function select(idx: number) {
    if (!results) return
    const sc = results.schools.length
    if (idx < sc) {
      const sid = results.schools[idx].id
      nav(`/schools?school_id=${sid}`)
      // 埋点:用户点击了搜索结果中的学校(fire-and-forget)
      trackEvent({ event_type: 'search_school', school_id: sid }).catch(() => {})
    } else {
      const m = results.majors[idx - sc]
      nav(`/schools?major=${encodeURIComponent(m.major_name)}`)
      // 埋点:用户点击了搜索结果中的专业(带 major_name 供 /api/site/hot 聚合)
      trackEvent({ event_type: 'view_major', major_name: m.major_name }).catch(() => {})
    }
    setFocused(false)
    setQ('')
  }

  return (
    <div ref={ref} style={{ position: 'relative', maxWidth: 600, margin: '2rem auto 0' }}>
      <input
        className="inp"
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={onKeyDown}
        placeholder="搜学校名 或 专业名 …"
        style={{
          fontSize: '1.125rem',
          padding: '.75rem 1rem',
          borderColor: focused ? 'var(--ink)' : 'var(--border)',
          width: '100%',
          transition: 'border-color 150ms',
        }}
      />
      {show && (
        <div
          className="card"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            zIndex: 20,
            maxHeight: 380,
            overflowY: 'auto',
          }}
        >
          {loading && <div style={{ padding: '.75rem', fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>搜学校 / 专业中…</div>}
          {err && <div className="alert-err" style={{ margin: '.5rem' }}>{err}</div>}
          {results && total === 0 && (
            <div style={{ padding: '.75rem', fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>没搜到。换两个字试试,或只输学校简称</div>
          )}
          {results && results.schools.length > 0 && (
            <div>
              <div className="eyebrow" style={{ padding: '.5rem .75rem .25rem' }}>
                学校 · {results.schools.length} 所
              </div>
              {results.schools.map((s, i) => (
                <div
                  key={s.id}
                  onClick={() => select(i)}
                  style={{
                    padding: '.5rem .75rem',
                    cursor: 'pointer',
                    background: selectedIdx === i ? 'var(--bg-deep)' : 'transparent',
                    fontSize: 'var(--fs-body)',
                    borderBottom: i < results.schools.length - 1 ? '1px solid var(--border)' : 'none',
                  }}
                  onMouseEnter={() => setSelectedIdx(i)}
                >
                  <span style={{ fontWeight: 600 }}>{s.school_name}</span>
                </div>
              ))}
            </div>
          )}
          {results && results.majors.length > 0 && (
            <div>
              <div
                className="eyebrow"
                style={{
                  padding: '.5rem .75rem .25rem',
                  borderTop: results.schools.length > 0 ? '1px solid var(--rule)' : 'none',
                }}
              >
                专业 · {results.majors.length} 个
              </div>
              {results.majors.map((m, i) => {
                const idx = results.schools.length + i
                return (
                  <div
                    key={m.major_name}
                    onClick={() => select(idx)}
                    style={{
                      padding: '.5rem .75rem',
                      cursor: 'pointer',
                      background: selectedIdx === idx ? 'var(--bg-deep)' : 'transparent',
                      fontSize: 'var(--fs-body)',
                      borderBottom: i < results.majors.length - 1 ? '1px solid var(--border)' : 'none',
                    }}
                    onMouseEnter={() => setSelectedIdx(idx)}
                  >
                    <span style={{ fontWeight: 600 }}>{m.major_name}</span>
                    <span style={{ marginLeft: '.5rem', fontSize: 'var(--fs-capt)', color: 'var(--ink3)' }}>
                      {m.school_count} 校
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
